/**
 * Deskon custom: the "Saved items" wishlist. The LIST is client-side - which handles are saved
 * lives in localStorage under `deskon-wishlist` and Shopify knows nothing about it - but what each
 * row SHOWS is read back from the shop, see "THE SNAPSHOT IS A CACHE" below.
 *
 * The drawer's SHELL is server-rendered Liquid (snippets/header-actions.liquid): the dialog, the
 * heading, the close button, the [data-wishlist-list] container and the "No saved items yet."
 * paragraph. Its CONTENTS - every [data-wishlist-item] row - are built here.
 *
 * =============================================================================================
 * THE BADGE SAID 1 AND THE DRAWER SAID "NO SAVED ITEMS YET". THIS IS WHY, AND IT IS THE WHOLE
 * REASON THE FUNCTIONS BELOW ARE SHAPED THE WAY THEY ARE.
 * =============================================================================================
 * The header re-renders itself through the Section Rendering API. Not rarely - at least once on
 * EVERY page load:
 *
 *     sections/header.liquid   ->  hydrate('{{ section.id }}', url)
 *     section-hydration.js     ->  sectionRenderer.renderSection(id, { cache: false, url })
 *     section-renderer.js      ->  fetch('<url>?section_id=<header>')  ->  morphSection(...)
 *
 * and again on every cart change, because snippets/header-actions.liquid puts
 * <cart-items-component data-section-id="{{ section.id }}"> INSIDE the header section, so
 * component-cart-items.js morphs the whole header - drawer included - on add to cart, on a
 * quantity change, on a line removal. THE ADD-TO-CART BUTTON ADDED BELOW FIRES THAT SAME PATH,
 * so every one of its clicks now destroys the rows it was clicked in. It survives only because
 * of the repair crew at the bottom of this file.
 *
 * What comes back from the server is the EMPTY shell, because the server cannot know what is in
 * localStorage. assets/morph.js then reconciles the live DOM to it, and does exactly two things
 * to this drawer:
 *
 *     "There is no new child, remove old"   ->  every injected .wishlist-item is deleted
 *     copyAttributes()                      ->  the hidden attribute is stripped off the
 *                                               empty-state <p>, so it becomes visible again
 *
 * The badge survived all this only because the observer at the bottom repaired it. The drawer did
 * not, because that observer deliberately called syncUI() - badge and toggle buttons only - and
 * never renderDrawer().
 *
 * WHY IT LOOKED LIKE A "ONE ITEM" BUG. The rows only survive if the last full refresh happened
 * AFTER the morph, and after page load the only thing that refreshed them was a save/remove
 * click. Reaching two items means clicking a toggle on the page you are on, and that click's
 * refresh redraws both rows after hydration has finished - so two looked fine. A single item
 * saved on an earlier page or an earlier visit was drawn once at startup, wiped by hydration a
 * moment later, and never drawn again. Nothing in the code ever tested the count.
 *
 * ---------------------------------------------------------------------------------------------
 * THE FIX: renderDrawer IS IDEMPOTENT, SO THE OBSERVER CAN CALL IT.
 *
 * An older note here said rebuilding the drawer from the observer would loop for ever - rebuild
 * is a DOM write, the write wakes the observer, the observer rebuilds. That was true of the old
 * unconditional rebuild, and it is why the drawer was excluded. It is no longer true.
 *
 * renderDrawer compares a SIGNATURE of what the rows should say against a signature of what they
 * do say, and touches nothing when they agree - the same guarded-write discipline updateBadge and
 * updateToggleButtons already used. So the sequence after a morph terminates:
 *
 *     morph deletes the rows  ->  observer  ->  signatures differ  ->  rows rebuilt (one write)
 *     that write             ->  observer  ->  signatures agree   ->  nothing written. Stop.
 *
 * Two ticks, never more. EVERY WRITE IN THIS FILE MUST STAY GUARDED BY A COMPARISON. If you add
 * an unconditional DOM write to any function the observer reaches, you reintroduce the loop the
 * old comment warned about.
 *
 * THE SIGNATURE IS NOT JUST THE HANDLES ANY MORE, and that is what lets everything below work
 * without a single unguarded write. It is handle + fetch state + "added" flag per row, so a row
 * is rebuilt exactly when one of those three changes - live prices arriving, a fetch failing, the
 * "Added" confirmation appearing and expiring. Anything new that changes what a row SAYS must be
 * added to rowSignature, or the row will not redraw when it changes.
 * ---------------------------------------------------------------------------------------------
 * A NOTE FOR WHOEVER TOUCHES snippets/header-actions.liquid. There is a second, stronger fix
 * available that this file cannot apply to itself: assets/morph.js honours
 *
 *     if (oldNode.hasAttribute('data-skip-subtree-update') && newNode.hasAttribute(...)) return;
 *
 * so putting data-skip-subtree-update on the [data-wishlist-list] container would make the morph
 * skip the drawer's contents entirely - preventing the damage instead of repairing it. It has to
 * be in the Liquid, because the attribute must be present on BOTH the live node and the freshly
 * fetched one. Worth doing if the drawer is ever seen flickering; the repair below is what makes
 * it unnecessary today.
 *
 * =============================================================================================
 * THE SNAPSHOT IS A CACHE, NOT THE TRUTH. WHY AN OPEN DRAWER REFETCHES.
 * =============================================================================================
 * Saving an item copies title, price, image and url out of the toggle button's data-* attributes.
 * That was enough while a row showed a name and a number. It was already measurably wrong - a
 * "Table Lamp - Olfen" saved before the September retarifing still read GBP 79.00 against a real
 * GBP 96.00 - and two of the three things asked for here cannot come from it at all: the struck
 * compare-at price was never captured, and adding to cart needs a VARIANT id, a variant COUNT and
 * a stock state.
 *
 * So an open drawer reads /products/<handle>.js - a few hundred bytes, no app, no API key - and
 * draws from that. The snapshot is painted first so the drawer never opens empty, then replaced,
 * and THE RESULT IS WRITTEN BACK INTO localStorage so the next open has nothing to correct.
 *
 * Three limits keep that honest: fetches only start while the dialog is actually OPEN (twenty
 * saved items must not mean twenty requests on every page), PENDING holds the in-flight promise
 * so the observer's many calls cannot multiply it, and a failure is cached as null so a deleted
 * product is not retried for ever.
 */
(() => {
  const STORAGE_KEY = 'deskon-wishlist';

  /* A Shopify product handle is lowercase alphanumerics and hyphens only, so a pipe can never
     occur inside one and two different lists can never collapse to the same signature.
     Deliberately a printable character: a control character here was silently decoded into a
     raw invisible byte on its way into the theme, which is exactly the sort of thing that
     survives review and then breaks in an editor. */
  const SIGNATURE_SEPARATOR = '|';

  /** handle -> product JSON, or null for "asked and failed". undefined means "never asked". */
  const PRODUCTS = new Map();
  /**
   * handle -> the in-flight promise. A MAP RATHER THAN A SET, deliberately: the observer calls
   * into here many times a second during a section morph and must not start a second request,
   * but a customer clicking "Add to cart" in that same window must get the REAL answer rather
   * than a "not loaded yet" null that would bounce them to the product page. Handing back the
   * promise already in flight serves both.
   */
  const PENDING = new Map();
  /** handles showing the transient "Added" confirmation, see ADDED_MS. */
  const RECENTLY_ADDED = new Set();
  const ADDED_MS = 1800;

  /**
   * Deskon: THE Array.isArray GUARD IS NOT DECORATION. Anything can end up under this key - an
   * older format, another script, a half-written value. Without it a stored string would reach
   * items.forEach, throw inside the click handler, and take the whole toggle down with it. A
   * shape we do not recognise is treated as an empty list, which is recoverable; a thrown error
   * in a delegated click listener is not.
   * @returns {Array<object>} the saved items, always an array
   */
  function getWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* localStorage unavailable (private mode, quota, etc.) - fail silently */
    }
  }

  function isSaved(handle) {
    return getWishlist().some((item) => item.handle === handle);
  }

  /**
   * Deskon: A MISSING HANDLE IS REFUSED. The handle is this list's only identity - it is what
   * isSaved, remove and the signature all match on. Storing an entry without one produces a row
   * that can never be found again, and an isSaved(undefined) that matches it for ever, so the
   * button it came from would read "saved" on every product on the site.
   */
  function toggle(data) {
    if (!data.handle) return;

    const items = getWishlist();
    const index = items.findIndex((item) => item.handle === data.handle);
    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push(data);
    }
    saveWishlist(items);
  }

  function remove(handle) {
    const items = getWishlist().filter((item) => item.handle !== handle);
    saveWishlist(items);
  }

  /**
   * Deskon: WRITES ARE GUARDED, and that is not a micro-optimisation.
   *
   * The observer at the bottom of this file re-runs these on any DOM change. If they wrote
   * unconditionally, every write would be a DOM change, which would wake the observer, which would
   * write again - an endless loop. Comparing before assigning is what makes them safe to call from
   * a mutation handler.
   */
  function updateToggleButtons() {
    const savedHandles = new Set(getWishlist().map((item) => item.handle));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const saved = savedHandles.has(button.dataset.handle) ? 'true' : 'false';
      if (button.dataset.saved !== saved) button.dataset.saved = saved;
      if (button.getAttribute('aria-pressed') !== saved) button.setAttribute('aria-pressed', saved);
    });
  }

  function updateBadge() {
    const count = String(getWishlist().length);
    const empty = count === '0';
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      if (el.textContent !== count) el.textContent = count;
      if (el.hidden !== empty) el.hidden = empty;
    });
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  /* ------------------------------------------------------------------------------------------
     LIVE PRODUCT DATA
     ------------------------------------------------------------------------------------------ */

  /**
   * Deskon: see "MONEY IS FORMATTED BY Intl" at the top. Minor units in, the shop's own currency
   * out. A null in - a product with no compare-at price - returns an empty string rather than
   * "£0.00", because the caller uses that emptiness to decide whether to draw the struck price.
   */
  function formatMoney(cents) {
    if (cents == null || cents === '') return '';
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency,
      }).format(Number(cents) / 100);
    } catch (e) {
      return '';
    }
  }

  /**
   * Deskon: the variant a customer would land on, not the cheapest one. See the note at the top.
   */
  function chosenVariant(product) {
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
    return product.variants.find((variant) => variant.available) || product.variants[0];
  }

  /**
   * Deskon: the CDN is asked for a narrow copy rather than the full 1080px original. The URL
   * already carries ?v=..., so the separator has to be decided rather than assumed - getting this
   * wrong produces a 404 image, not a big one.
   */
  function sizedImage(url, width) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /**
   * Deskon: WRITTEN BACK INTO STORAGE SO THE NEXT OPEN NEEDS NO CORRECTION - and guarded, because
   * an unconditional save on every render would be a write on every observer tick. Only the four
   * fields a row actually draws are compared; anything else the snapshot carries is left alone.
   */
  function persistProduct(handle, product) {
    const variant = chosenVariant(product);
    if (!variant) return;

    const next = {
      price: formatMoney(variant.price) || undefined,
      comparePrice: variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '',
      image: sizedImage(product.featured_image, 300) || undefined,
      title: product.title || undefined,
    };

    const items = getWishlist();
    const item = items.find((entry) => entry.handle === handle);
    if (!item) return;

    let changed = false;
    Object.keys(next).forEach((key) => {
      if (next[key] !== undefined && item[key] !== next[key]) {
        item[key] = next[key];
        changed = true;
      }
    });

    if (changed) saveWishlist(items);
  }

  /**
   * Deskon: one request per handle per page, failures cached as null. Resolves to the product or
   * null; never throws, because every caller is inside a click handler or the observer.
   */
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (PRODUCTS.has(handle)) return Promise.resolve(PRODUCTS.get(handle));
    if (PENDING.has(handle)) return PENDING.get(handle);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const request = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((product) => {
        PRODUCTS.set(handle, product || null);
        PENDING.delete(handle);
        if (product) persistProduct(handle, product);
        refreshAll();
        return product || null;
      });

    PENDING.set(handle, request);
    return request;
  }

  /** For the click handler: the product, fetching it first if the drawer was opened too fast. */
  function ensureProduct(handle) {
    return fetchProduct(handle);
  }

  /**
   * Deskon: THE FETCH GATE. True only while a drawer holding this list is actually open, so a
   * customer browsing with items saved costs nothing. dialog.open is the real state - the theme's
   * dialog-component opens a native <dialog>, so there is no class to guess at.
   */
  function drawerIsOpen(list) {
    const dialog = list.closest('dialog');
    return !!(dialog && dialog.open);
  }

  /* ------------------------------------------------------------------------------------------
     ROWS
     ------------------------------------------------------------------------------------------ */

  /**
   * Deskon: WHAT A ROW SHOULD SAY, in one string. Handle, fetch state, and whether the "Added"
   * confirmation is showing. renderDrawer rebuilds a list whenever this disagrees with the
   * data-sig it finds on screen, which is the only thing that makes the rebuild safe to call from
   * a mutation observer. ADD TO THIS whenever you add something to a row that can change on its
   * own, or the row will quietly stop updating.
   */
  function rowSignature(item) {
    const product = PRODUCTS.get(item.handle);
    const state = product === undefined ? 'p' : product === null ? 'f' : 'e';
    return item.handle + ':' + state + (RECENTLY_ADDED.has(item.handle) ? 'a' : '-');
  }

  function listSignature(items) {
    return items.map(rowSignature).join(SIGNATURE_SEPARATOR);
  }

  /**
   * What the drawer IS showing. Read from each row's own data-sig, set as a property rather than
   * written into the innerHTML template below - so no HTML-escaping round trip can make a row's
   * signature differ from the computed one and leave the two permanently unequal, which would
   * rebuild the drawer on every single mutation.
   */
  function renderedSignature(list) {
    return Array.from(list.querySelectorAll('[data-wishlist-item]'))
      .map((row) => row.dataset.sig || '')
      .join(SIGNATURE_SEPARATOR);
  }

  /**
   * Deskon: THE FOUR STATES OF THE BUTTON, and why there are four rather than one.
   *
   *   not fetched yet  "Add to cart", live. A customer who clicks in the first 200ms must not hit
   *                    a dead control, so the handler awaits the fetch and then acts.
   *   one variant      "Add to cart". The only case that can add without asking anything.
   *   several variants "Choose options" -> the product page. Five colourways cannot be resolved
   *                    from a 72px thumbnail, and silently adding the first one is how a customer
   *                    ends up with the wrong lamp.
   *   sold out         disabled. Rendered rather than hidden: a missing button reads as a bug.
   *
   * A failed fetch falls back to "View product", which always works because the url is stored.
   */
  function buttonState(item) {
    const product = PRODUCTS.get(item.handle);

    if (product === undefined) return { label: 'Add to cart', action: 'add', disabled: false };
    if (product === null) return { label: 'View product', action: 'link', disabled: false };

    const variant = chosenVariant(product);
    if (!variant || !variant.available) return { label: 'Sold out', action: 'none', disabled: true };
    if (product.variants.length > 1) return { label: 'Choose options', action: 'link', disabled: false };
    return { label: 'Add to cart', action: 'add', disabled: false };
  }

  function buildRow(item) {
    const product = PRODUCTS.get(item.handle);
    const variant = product ? chosenVariant(product) : null;

    /* Live figures when we have them, the stored snapshot until then - never nothing. */
    const price = variant ? formatMoney(variant.price) : item.price;
    const compare =
      variant && variant.compare_at_price > variant.price
        ? formatMoney(variant.compare_at_price)
        : variant
          ? ''
          : item.comparePrice || '';
    const image = product ? sizedImage(product.featured_image, 240) : item.image;
    const added = RECENTLY_ADDED.has(item.handle);
    const button = buttonState(item);

    const row = document.createElement('div');
    row.className = 'wishlist-item';
    row.setAttribute('data-wishlist-item', '');
    row.dataset.handle = item.handle == null ? '' : String(item.handle);
    row.dataset.sig = rowSignature(item);

    row.innerHTML = `
      <a href="${escapeHtml(item.url)}" class="wishlist-item__media" tabindex="-1" aria-hidden="true">
        <img src="${escapeHtml(image)}" alt="" loading="lazy" width="72" height="90">
      </a>
      <div class="wishlist-item__info">
        <a href="${escapeHtml(item.url)}" class="wishlist-item__title">${escapeHtml(item.title)}</a>
        <span class="wishlist-item__prices">
          <span class="wishlist-item__price price">${escapeHtml(price)}</span>
          ${compare ? `<s class="wishlist-item__compare compare-at-price">${escapeHtml(compare)}</s>` : ''}
        </span>
        <button
          type="button"
          class="wishlist-item__add"
          data-wishlist-add
          data-handle="${escapeHtml(item.handle)}"
          data-action="${escapeHtml(button.action)}"
          ${button.disabled ? 'disabled' : ''}
          ${added ? 'data-added="true"' : ''}
        >${added ? 'Added' : escapeHtml(button.label)}</button>
      </div>
      <button type="button" class="wishlist-item__remove" data-wishlist-remove data-handle="${escapeHtml(item.handle)}" aria-label="Remove ${escapeHtml(item.title)} from saved items">&times;</button>
    `;
    return row;
  }

  /**
   * Deskon: IDEMPOTENT BY CONTRACT - see the long note at the top. It writes only when what is on
   * screen disagrees with what it should say, which is what makes it safe to call from the
   * mutation observer, which is what stops a header morph leaving an empty drawer behind.
   *
   * The empty state is repaired SEPARATELY from the rows, and it has to be. morph strips the
   * hidden attribute off that paragraph even in the case where it leaves nothing else to do, so
   * checking it only when the rows are rebuilt would miss exactly the situation this fixes.
   */
  function renderDrawer() {
    const items = getWishlist();
    const signature = listSignature(items);
    const hideEmptyState = items.length > 0;

    document.querySelectorAll('[data-wishlist-list]').forEach((list) => {
      if (renderedSignature(list) !== signature) {
        list.querySelectorAll('[data-wishlist-item]').forEach((el) => el.remove());
        items.forEach((item) => list.appendChild(buildRow(item)));
      }

      const emptyState = list.querySelector('[data-wishlist-empty]');
      if (emptyState && emptyState.hidden !== hideEmptyState) emptyState.hidden = hideEmptyState;

      /* The gate, not a side effect of rendering: only an OPEN drawer pays for live data. */
      if (drawerIsOpen(list)) {
        items.forEach((item) => {
          if (!PRODUCTS.has(item.handle)) fetchProduct(item.handle);
        });
      }
    });
  }

  /* ------------------------------------------------------------------------------------------
     ADD TO CART
     ------------------------------------------------------------------------------------------ */

  /**
   * Deskon: A DELIBERATE COPY OF assets/product-form.js's submit path. That path lives inside
   * <product-form-component> and is driven by a real <form> with variantId and addToCartButton
   * refs; there is no such form in this drawer, and faking one would be more code, not less. What
   * matters is that the three things the rest of the theme depends on are reproduced exactly:
   *
   *   1 THE sections FIELD. Every <cart-items-component> publishes a data-section-id; those ids
   *     go up with the request and come back rendered in response.sections. Without it the cart
   *     drawer and the header badge have nothing to redraw themselves from.
   *   2 THE HEADERS. X-Requested-With plus an Accept that is not application/json - the pair
   *     fetchConfig('javascript') builds and product-form.js then overrides to text/html. The
   *     response is JSON either way.
   *   3 THE EVENT. CartAddEvent from @theme/events is simply an Event named "cart:update" that
   *     bubbles and carries { resource, sourceId, data }. Built by hand rather than imported so
   *     this file survives the import map moving, and dispatched on the document element so it
   *     reaches the listeners bound to document.
   *
   * THE ROUTE IS Shopify.routes.root + 'cart/add.js'. The Theme global that product-form.js
   * closes over is not exposed on window - checked, not assumed.
   *
   * A REFUSED ADD RE-READS THE PRODUCT: the likeliest reason is that it sold out since the JSON
   * was cached, so the entry is dropped and refetched and the button redraws itself as "Sold out"
   * through the ordinary signature path rather than a special case here.
   */
  function addToCart(handle, variantId) {
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response && response.status) {
          PRODUCTS.delete(handle);
          PENDING.delete(handle);
          fetchProduct(handle);
          return;
        }

        const event = new Event('cart:update', { bubbles: true });
        event.detail = {
          resource: {},
          sourceId: 'deskon-wishlist',
          data: {
            source: 'deskon-wishlist',
            itemCount: 1,
            productId: response && response.product_id ? String(response.product_id) : '',
            sections: response && response.sections,
          },
        };
        document.documentElement.dispatchEvent(event);

        /* The confirmation is state, not a DOM edit: the header morph this add just triggered is
           about to delete the row it would have been written into. Held in RECENTLY_ADDED, drawn
           by buildRow, expired by the timer - all of it through the signature. */
        RECENTLY_ADDED.add(handle);
        refreshAll();
        setTimeout(() => {
          RECENTLY_ADDED.delete(handle);
          refreshAll();
        }, ADDED_MS);
      })
      .catch(() => {});
  }

  /**
   * Deskon: flies a clone of the filled bookmark icon from the button that was
   * just clicked to the wishlist icon in the header, reusing the theme's native
   * <fly-to-cart> custom element (assets/fly-to-cart.js) that already powers the
   * "add to cart -> cart icon" flight animation. It's loaded globally on every
   * page, so this works the same way. Only runs when SAVING an item (not when
   * un-saving), and bails out quietly if any piece isn't available.
   * @param {HTMLElement} sourceButton - The [data-wishlist-toggle] button that was clicked.
   */
  function animateFlyToWishlist(sourceButton) {
    if (!sourceButton || !customElements.get('fly-to-cart')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const destination = document.querySelector('.wishlist-header-icon');
    if (!destination) return;

    const filledIcon = sourceButton.querySelector('[class*="icon--filled"]');
    if (!filledIcon || !filledIcon.innerHTML) return;

    const flyEl = document.createElement('fly-to-cart');
    flyEl.classList.add('fly-to-wishlist');
    flyEl.innerHTML = filledIcon.innerHTML;
    flyEl.source = sourceButton;
    flyEl.destination = destination;

    document.body.appendChild(flyEl);
  }

  /** Everything the UI shows, brought back in line with storage. All writes guarded. */
  function refreshAll() {
    updateToggleButtons();
    updateBadge();
    renderDrawer();
  }

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-wishlist-toggle]');
    if (toggleButton) {
      const wasSaved = isSaved(toggleButton.dataset.handle);

      toggle({
        handle: toggleButton.dataset.handle,
        title: toggleButton.dataset.title,
        price: toggleButton.dataset.price,
        image: toggleButton.dataset.image,
        url: toggleButton.dataset.url,
      });

      if (!wasSaved) {
        animateFlyToWishlist(toggleButton);
      }

      refreshAll();
      return;
    }

    const addButton = event.target.closest('[data-wishlist-add]');
    if (addButton) {
      const handle = addButton.dataset.handle;
      const stored = getWishlist().find((entry) => entry.handle === handle);

      /* Awaited rather than read, for the customer who clicks before the JSON lands. */
      ensureProduct(handle).then((product) => {
        if (!product) {
          if (stored && stored.url) window.location.href = stored.url;
          return;
        }

        const variant = chosenVariant(product);
        if (!variant || !variant.available) {
          refreshAll();
          return;
        }

        if (product.variants.length > 1) {
          if (stored && stored.url) window.location.href = stored.url;
          return;
        }

        addToCart(handle, variant.id);
      });
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      remove(removeButton.dataset.handle);
      refreshAll();
    }
  });

  /**
   * Deskon: THE OBSERVER IS THE REPAIR CREW. It exists because the theme replaces this drawer and
   * this badge behind our back - see the note at the top for the code paths that do it, one of
   * which is now the add-to-cart button in this very drawer.
   *
   * IT CALLS refreshAll(), INCLUDING THE DRAWER. That was the original bug: it used to call a
   * badge-only sync, so the badge came back and the rows did not. refreshAll is safe here only
   * because every function it calls compares before it writes.
   *
   * IT ALSO WATCHES THE open ATTRIBUTE, and only that one. Opening the drawer is what starts the
   * live-price fetches, and a native <dialog> opening is an attribute change, not a child list
   * change - without the filter the drawer would sit there showing stale snapshot prices until
   * something else happened to mutate the page. attributeFilter is doing real work here: an
   * unfiltered attributes:true would wake this on every data-sig and hidden this file writes.
   *
   * IT IS DEBOUNCED TO ONE FRAME. A section swap fires many mutations in a row; without this the
   * handler would run dozens of times for a single cart update.
   *
   * shopify:section:load IS KEPT ALONGSIDE IT. The observer covers it in practice, but that event
   * is what the theme editor fires when a section is edited, and it is cheap insurance.
   *
   * pageshow COVERS THE BACK BUTTON. A page restored from the bfcache does not re-run
   * DOMContentLoaded, so a customer returning to a listing would otherwise see a stale badge -
   * and, before this change, a drawer still holding whatever it held when they left.
   */
  let queued = false;
  function scheduleRefresh() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      refreshAll();
    });
  }

  function start() {
    refreshAll();

    if (window.MutationObserver) {
      new MutationObserver(scheduleRefresh).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    document.addEventListener('shopify:section:load', refreshAll);
    window.addEventListener('pageshow', refreshAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.deskonWishlist = {
    refresh: refreshAll,
    items: getWishlist,
  };
})();
